import {
  ApprovedFieldProjection,
  ApprovedFieldUsageProjection,
} from "@/api/adminApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { BackButton, PageHeader, Pill } from "./shared";

export function FieldUsageView({
  field,
  usage,
  loading,
  onBack,
}: {
  field: ApprovedFieldProjection | null;
  usage: ApprovedFieldUsageProjection | null;
  loading: boolean;
  onBack: () => void;
}) {
  if (!field) {
    return (
      <>
        <PageHeader title="Field usage" subtitle="Select an approved field first." action={<BackButton onClick={onBack} />} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={`${field.label} usage`}
        subtitle={field.review_impact || "No additional review"}
        action={<BackButton onClick={onBack} />}
      />
      <div className="space-y-4 p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Programs using this field</div>
              <div className="mt-1 text-3xl font-semibold">{usage?.program_count ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Application forms scanned</div>
              <div className="mt-1 text-3xl font-semibold">{usage?.total_applications ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Status</div>
              <div className="mt-2">
                <Pill tone={field.active ? "green" : "amber"}>
                  {field.active ? "Active" : "Retired"}
                </Pill>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Used in these programs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-sm text-gray-600">Loading usage...</div>
            ) : usage && usage.programs.length > 0 ? (
              usage.programs.map((program) => (
                <div
                  key={`${program.tenant_id}-${program.program_code}-${program.academic_year}-${program.form_name}`}
                  className="flex flex-wrap items-center justify-between gap-3 border-t p-4 first:border-t-0"
                >
                  <div>
                    <div className="font-medium">{program.form_name}</div>
                    <div className="text-xs text-gray-500">
                      {program.program_code} · {program.academic_year} ·{" "}
                      {program.active ? "Active" : "Inactive"}
                    </div>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="text-lg font-semibold">{program.applications}</div>
                      <div className="text-xs text-gray-500">Applications</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{program.re_reviews_triggered}</div>
                      <div className="text-xs text-gray-500">Re-reviews</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-sm text-gray-600">
                This approved field is not used in any active program yet.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Field justification</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-700">
            {field.why_needed || field.description || "No justification recorded."}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Standards summary</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-3">
            <div>
              <div className="text-xs text-gray-500">Category</div>
              <div>{field.category}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Importance</div>
              <div>{field.importance}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Platform locked</div>
              <div>{field.platform_locked ? "Yes" : "No"}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
